import React, { useCallback, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Trash2 } from 'lucide-react';

import { getRecentAssets, MAX_ITEMS, removeRecentAsset } from '@/shared/lib/storage/recentAssets';
import type { AssetListAction, RecentAssetItem } from '@/shared/types/assetList';
import { AssetList } from '@/shared/ui/AssetList';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';

import styles from './RecentAssetsTab.module.css';

dayjs.extend(relativeTime);

type RecentAssetsTabProps = {
  maxItems?: number;
  onAssetClick: (asset: RecentAssetItem) => void;
  showActions?: boolean;
};

export const RecentAssetsTab: React.FC<RecentAssetsTabProps> = ({ maxItems = MAX_ITEMS, onAssetClick }) => {
  const [recentAssets, setRecentAssets] = useState<RecentAssetItem[]>([]);

  const loadRecentAssets = useCallback(() => {
    const assets = getRecentAssets();
    const recentItems: RecentAssetItem[] = assets.slice(0, maxItems);
    setRecentAssets(recentItems);
  }, [maxItems]);

  useEffect(() => {
    loadRecentAssets();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'cyclone_recent_assets') {
        loadRecentAssets();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadRecentAssets]);

  const handleRecentAssetDelete = (assetId: string) => {
    removeRecentAsset(assetId);
    loadRecentAssets();
  };

  const getActions = (item: RecentAssetItem): AssetListAction[] => [
    {
      key: 'delete',
      icon: <Trash2 size={16} />,
      onClick: () => handleRecentAssetDelete(item.id),
      className: styles.deleteButton,
    },
  ];

  return (
    <div className={styles.tabWrapper}>
      <NoDataBoundary
        data={recentAssets}
        emptyDescription="최근 본 항목이 없습니다. 데이터 자산을 조회하면 여기에 표시됩니다">
        <div className={styles.tabContent}>
          <div className={styles.listContainer}>
            <AssetList
              items={recentAssets}
              getActions={getActions}
              getMetadata={(item) => dayjs(item.viewedAt).fromNow()}
              onItemClick={onAssetClick}
            />
          </div>
        </div>
      </NoDataBoundary>
    </div>
  );
};
